import { useState } from 'react';
import { Link } from 'react-router-dom';
import { UsersIcon } from 'lucide-react';
import { fetchInsightsCustomers } from '../../adminApi';
import { Panel } from '../../ui/Panel';
import { btnGhost, btnPrimary } from '../../ui/styles';
import { ADMIN_BASE } from '../../../utils/hosts';
import { dayShort, monthShort } from '../../../core/format/dates';
import { plural } from '../../../core/format/text';
import { ChartFrame } from '../charts/ChartFrame';
import { BarChart } from '../charts/BarChart';
import { Kpi } from '../Kpi';
import { ReportView } from '../ReportView';
import { useReport } from '../useReport';
import { fmt } from '../insightFormat';
import { CohortTable } from './CohortTable';
import type { PeriodState } from '../../sales/usePeriodParams';
const shapeText = {
  salon: 'Salon',
  webshop: 'Webshop',
  beide: 'Salon en webshop'
} as const;
const SHOWN = 6;

/** Klanten: wie er nieuw is, wie terugkomt, wat een klant waard is en wie wegglijdt. */
export function CustomersTab({
  period
}: {
  period: PeriodState;
}) {
  const report = useReport(fetchInsightsCustomers, period.from, period.to);
  const [all, setAll] = useState(false);
  return <ReportView report={report} what="De klantcijfers" data-id="element-2021" data-mp="1b3xauwfv4vg6">
      {c => <>
          <section aria-label="Klantwaarde" className="rounded-lg border border-line bg-white p-5 sm:p-6" data-id="element-2022" data-mp="xa639tlozln3">
            <dl className="grid grid-cols-1 gap-6 sm:grid-cols-3" data-id="element-2023" data-mp="119x0af1tay02">
              <Kpi size="lg" label="Gemiddelde besteding per klant" value={c.averageSpendCents} kind="money" data-id="element-2024" data-mp="nbgv4sjllfk1" />
              <div className="flex flex-col" data-id="element-2025" data-mp="scwemg64ne0b">
                <dt className="text-[12px] text-muted" data-id="element-2026" data-mp="ebtqlxvkntmr">Klantwaarde in de eerste 12 maanden</dt>
                <dd className="mt-1 font-display text-[1.9rem] font-light leading-none tabular-nums text-ink" data-id="element-2027" data-mp="id5he6q6mkwj">{fmt(c.value12mCents, 'money')}</dd>
                <dd className="mt-1 text-[12px] text-muted" data-id="element-2028" data-mp="28qwbfi9oenjc">Gemiddeld, over {plural(c.value12mCustomers, 'klant', 'klanten')} die al een jaar klant zijn</dd>
              </div>
              <div className="flex flex-col" data-id="element-2029" data-mp="3lodcu6txjp04">
                <dt className="text-[12px] text-muted" data-id="element-2030" data-mp="3lm0lx46724ax">Tijd tot de tweede aankoop</dt>
                <dd className="mt-1 font-display text-[1.9rem] font-light leading-none tabular-nums text-ink" data-id="element-2031" data-mp="3gnh1573c6yk5">
                  {c.medianDaysToSecond === null ? '–' : `${c.medianDaysToSecond} dagen`}
                </dd>
                <dd className="mt-1 text-[12px] text-muted" data-id="element-2032" data-mp="37qosd03ryjvl">Mediaan, over {plural(c.secondPurchaseCustomers, 'klant', 'klanten')}</dd>
              </div>
            </dl>
          </section>

          <ChartFrame title="Nieuw en terugkerend" description="Per maand: eerste aankoop of behandeling, tegenover klanten die terugkwamen. Webshop en salon samen." fileName={`klanten-per-maand-${period.to}`} table={{
        columns: [{
          label: 'Maand'
        }, {
          label: 'Nieuw'
        }, {
          label: 'Terugkerend'
        }],
        rows: c.months.map(m => [monthShort(m.month), m.new, m.returning])
      }} data-id="element-2033" data-mp="49q4eex7qbbu">
            <BarChart bars={c.months.map(m => ({
          key: m.month,
          label: monthShort(m.month).split(' ')[0],
          values: [m.returning, m.new]
        }))} series={[{
          label: 'Terugkerend',
          className: 'bg-ink/75'
        }, {
          label: 'Nieuw',
          className: 'bg-clay/75'
        }]} format={n => fmt(n)} data-id="element-2034" data-mp="uixkokxs5x2y" />
          </ChartFrame>

          <div className="grid gap-6 lg:grid-cols-2" data-id="element-2035" data-mp="1dfgk2iis86mb">
            <Panel title="Wegglijdend" description={`Langer dan ${c.slipping.weeks} weken geen bezoek of bestelling, en niets gepland.`} actions={c.slipping.customers.length > 0 ? <Link to={`${ADMIN_BASE}/mail?tab=abonnees&doelgroep=terugzien&weken=${c.slipping.weeks}`} className={btnPrimary} data-id="element-2037" data-mp="1s431cpryz8g6">
                    <UsersIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2038" data-mp="1w4wr7ooz2xbp" />
                    Maak doelgroep
                  </Link> : undefined} data-id="element-2036" data-mp="1ifl3agb2rfg8">
              {c.slipping.customers.length === 0 ? <p className="text-[13px] text-muted" data-id="element-2039" data-mp="1jgp82c9g6677">Niemand: iedereen is recent geweest of heeft iets gepland.</p> : <>
                  <p className="mb-3 text-[13px] text-ink" data-id="element-2040" data-mp="1sa29y3phoi5f">{plural(c.slipping.customers.length, 'klant', 'klanten')}</p>
                  <ul className="divide-y divide-line text-[13px]" data-id="element-2041" data-mp="14jybsbv2lalt">
                    {(all ? c.slipping.customers : c.slipping.customers.slice(0, SHOWN)).map(s => <li key={`${s.id ?? s.name}`} className="flex items-baseline justify-between gap-3 py-2" data-id="element-2042" data-mp="1einrpz3eokzf">
                        {s.id ? <Link to={`${ADMIN_BASE}/klanten/${s.id}`} className="truncate text-ink underline-offset-4 hover:text-clay hover:underline" data-id="element-2043" data-mp="19it2dzza43gh">
                            {s.name}
                          </Link> : <span className="truncate text-ink" data-id="element-2044" data-mp="6qyn91umpepe">{s.name}</span>}
                        <span className="shrink-0 text-muted" data-id="element-2045" data-mp="bq1soneitmxw">
                          {shapeText[s.shape]} · laatst {dayShort(s.lastSeen)}
                        </span>
                      </li>)}
                  </ul>
                  {c.slipping.customers.length > SHOWN && <button type="button" onClick={() => setAll(v => !v)} className={`${btnGhost} -ml-2.5 mt-2`} aria-expanded={all} data-id="element-2046" data-mp="qf7pk4pvz12m">
                      {all ? 'Minder tonen' : `Alle ${c.slipping.customers.length} tonen`}
                    </button>}
                  <p className="mt-3 text-[12px] leading-relaxed text-muted" data-id="element-2047" data-mp="gn1w0qjzkblb">De doelgroep is voor de nieuwsbrief: alleen wie die ontvangt, valt erin. Je kunt hem aanpassen voor je opslaat.</p>
                </>}
            </Panel>

            <Panel title="Komen ze terug?" description="Van de klanten die in een maand begonnen: hoeveel kwam daarna nog eens." data-id="element-2048" data-mp="1e4s84mi4nwbd">
              <CohortTable cohorts={c.cohorts} data-id="element-2049" data-mp="1o00lc0exfite" />
            </Panel>
          </div>
        </>}
    </ReportView>;
}